import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';
import { IconJsxer } from '../../../helpers/IconHelper';
import "./DropDownList.scss";

const AdvancedDropDownList = ({
    id,
    items,
    currentIndex,
    selectedItems,
    selectedClass,
    deselectedClass,
    onSelect,
    summary,
    icon,
    placeholder,
    submenuOptionsFunc,
    onSubmenuSelect,
    getSelectedSubmenu,
    className,
    style,
    enabled,
    closeOnSelect = false,
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [submenuOpenFor, setSubmenuOpenFor] = useState(null);
    const [submenuAnchor, setSubmenuAnchor] = useState(null);

    const dropdownRef = useRef();

    const selected = !items[currentIndex]?.deselect;

    // Filter items by search and show label fallback for clientName
    const filteredItems = items.filter(item => {
        const label = item.label || item.name || item.clientName || '';
        return label.toLowerCase().includes(search.toLowerCase());
    });

    const closeEntireDropdown = () => {
        setOpen(false);
        setSubmenuOpenFor(null);
        setSubmenuAnchor(null);
    }
    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(e) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
               closeEntireDropdown();
            }
        }
        if (open) document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, [open]);

    // Open submenu for item (on row or arrow click)
    const handleOpenSubmenu = (item, e) => {
        e.stopPropagation();
        const itemRect = e.currentTarget.getBoundingClientRect();
        const containerRect = dropdownRef.current.getBoundingClientRect();
        // Calculate position relative to the drop-list-holder container
        const top = itemRect.top - containerRect.top;
        const left = containerRect.width;
        setSubmenuOpenFor(item);
        setSubmenuAnchor({ top, left, height: itemRect.height });
    };

    // Submenu select
    const handleSubmenuSelect = (submenuOption) => {
        if (onSubmenuSelect && submenuOpenFor) {
            onSubmenuSelect(submenuOpenFor, submenuOption);
        }
        setSubmenuOpenFor(null);
        setSubmenuAnchor(null);
    };

    // Clear search
    const clearSearch = (e) => {
        e.stopPropagation();
        setSearch('');
    };

    // Always treat as people dropdown if submenuOptionsFunc returns ["owner", "shared", "either"]
    const isPeopleDropdown = !!submenuOptionsFunc && items.length > 0 && (() => {
        const opts = submenuOptionsFunc(items[0]);
        return Array.isArray(opts) && opts.length === 3 && opts.includes("owner") && opts.includes("shared") && opts.includes("either");
    })();

    // Helper: is item selected (people or client)
    const isSelected = (item) => {
        if (item._id) return selectedItems.some(sel => sel._id === item._id);
        if (item.clientName) return selectedItems.some(sel => sel.clientName === item.clientName);
        return false;
    };

    // Helper: get bounding rect for submenu positioning
    const getRect = (el) => {
        if (!el || !dropdownRef.current) return { top: 0, left: 0, height: 0 };
        const itemRect = el.getBoundingClientRect();
        const containerRect = dropdownRef.current.getBoundingClientRect();
        // Calculate position relative to the drop-list-holder container
        const top = itemRect.top - containerRect.top;
        const left = containerRect.width;
        return { top, left, height: itemRect.height };
    };

    return (
        <div className={`drop-list-holder${className ? ` ${className}` : ''}`} style={style} ref={dropdownRef}>
            <div
                className={`drop-down-closed top-row ${selected ? selectedClass : deselectedClass} ${!enabled ? "drop-down-disabled" : ""}`}
                onClick={() => (open) ? closeEntireDropdown() : setOpen(!open)}
                style={{ cursor: "pointer" }}
            >
                {(icon) ? IconJsxer.GetIcon(typeof icon === 'string' ? IconJsxer.ICONS[icon] : icon, IconJsxer.ICON_STYLES.roundFormControl) : null}
                <div className='label-holder'>{summary || placeholder}</div>
                {(enabled) ? IconJsxer.GetIcon(open ? IconJsxer.ICONS.chevronUp : IconJsxer.ICONS.chevronDown, IconJsxer.ICON_STYLES.roundFormControl) : null}
            </div>
            {open && (
                <div className="drop-down-list advanced-drop-down-list default-selected" style={{ position: "absolute", zIndex: 1000, minWidth: 220, maxHeight: "400px", overflow: "auto" }}>
                    {/* Search box */}
                    <div style={{ display: 'flex', alignItems: 'center', padding: '4px 8px', background: 'var(--panel-body-bg)' }}>
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder={placeholder}
                            className="advanced-dropdown-search-input"
                            onClick={e => e.stopPropagation()}
                        />
                        {search && (
                            <button
                                onClick={e => { e.stopPropagation(); setSearch(''); }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    marginLeft: 4,
                                    fontSize: 16,
                                    color: '#888'
                                }}
                                aria-label="Clear search"
                            >
                                ×
                            </button>
                        )}
                    </div>
                    {/* List */}
                    {filteredItems.map((item, idx) => {
                        const label = item.label || item.name || item.clientName || '';
                        const checked = isSelected(item);
                        const submenuOptions = submenuOptionsFunc ? submenuOptionsFunc(item) : [];
                        const hasSubmenu = submenuOptions && submenuOptions.length > 0;
                        // Highlight if submenu is open for this item
                        const isSubmenuOpen = submenuOpenFor && (
                            (item._id && submenuOpenFor._id && item._id === submenuOpenFor._id) ||
                            (item.clientName && submenuOpenFor.clientName && item.clientName === submenuOpenFor.clientName)
                        );
                        return (
                            <div
                                key={item._id || item.clientName || idx}
                                className="dropdown-list-item"
                                style={{
                                    background: isSubmenuOpen ? "var(--parity-grey-2)" : (checked ? "var(--panel-body-bg)" : "transparent"),
                                    position: "relative",
                                    display: "flex",
                                    alignSelf: "flex-start",
                                    justifyContent: "flex-start",
                                    paddingLeft: 8,
                                    paddingRight: 8,
                                    paddingTop: 4,
                                    paddingBottom: 4,
                                    cursor: "pointer",
                                    width: "100%",
                                    minHeight: "32px",
                                    alignItems: "center"
                                }}
                            >
                                <input
                                    type="checkbox"
                                    readOnly
                                    checked={checked}
                                    style={{ margin: "0 8px", cursor: "pointer" }}
                                    onClick={e => {
                                        if (checked) {
                                            e.stopPropagation();
                                            // Always allow deselect
                                            onSelect(item);
                                            if (closeOnSelect) closeEntireDropdown();
                                        } else if (!hasSubmenu) {
                                            // Select directly if no submenu
                                            onSelect(item);
                                            if (closeOnSelect) closeEntireDropdown();
                                        } else {
                                            handleOpenSubmenu(item, e);
                                        }
                                        // If not selected and has submenu, do nothing (let label/arrow open submenu)
                                    }}
                                />
                                <span
                                    style={{ flex: 1, textAlign: "left" }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        if (hasSubmenu) {
                                            const rect = getRect(e.currentTarget.parentElement);
                                            setSubmenuOpenFor(item);
                                            setSubmenuAnchor(rect);
                                        } else {
                                            onSelect(item);
                                            if (closeOnSelect) setOpen(false);
                                        }
                                    }}
                                >{label}</span>
                                {submenuOptionsFunc && getSelectedSubmenu && (
                                    <span style={{ marginLeft: 8, fontSize: 12, color: "#888", textAlign: "left" }}>
                                        {(getSelectedSubmenu(item) || []).join(", ")}
                                    </span>
                                )}
                                {hasSubmenu && (
                                    <span
                                        style={{ marginLeft: 8, cursor: "pointer" }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            const rect = getRect(e.currentTarget.parentElement);
                                            setSubmenuOpenFor(item);
                                            setSubmenuAnchor(rect);
                                        }}
                                    >
                                        <i className="fa fa-chevron-right" />
                                    </span>
                                )}
                            </div>
                        );
                    })}
                    <div className="dropdown-list-item close" onClick={() => closeEntireDropdown()}>Close</div>
                </div>
            )}
            {/* Submenu - rendered outside dropdown to avoid clipping */}
            {submenuOpenFor && submenuAnchor && (
                <div
                    className="dropdown-menu"
                    style={{
                        display: "block",
                        position: "absolute",
                        left: submenuAnchor.left + 70,
                        top: submenuAnchor.top,
                        minWidth: 200,
                        maxWidth: 260,
                        zIndex: 2000,
                        background: "var(--panel-body-bg)",
                        border: "1px solid var(--dropdown-border-color)",
                        borderRadius: 4,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                        padding: 0
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    {submenuOptionsFunc(submenuOpenFor).map(opt => {
                        const selected = (getSelectedSubmenu(submenuOpenFor) || []).includes(opt);
                        return (
                            <div
                                key={opt}
                                onClick={() => handleSubmenuSelect(opt)}
                                style={{
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    background: selected ? "var(--panel-body-bg)" : "var(--panel-body-bg)",
                                    display: "flex",
                                    alignItems: "center"
                                }}
                            >
                                <input
                                    type="checkbox"
                                    readOnly
                                    checked={selected}
                                    style={{ marginRight: 8 }}
                                    tabIndex={-1}
                                />
                                <span style={{ color: "var(--dropdown-text-color)" }}>{opt === "owner" ? "Owner" : opt === "shared" ? "Shared with" : opt}</span>
                                {selected && <i className="fa fa-check" style={{ marginLeft: 8 }} />}
                            </div>
                        );
                    })}
                    <div className="dropdown-list-item close" onClick={() => setSubmenuOpenFor(null)}>Cancel</div>
                </div>
            )}
        </div>
    );
};

AdvancedDropDownList.propTypes = {
    id: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
    selectedItems: PropTypes.array.isRequired,
    onSelect: PropTypes.func.isRequired,
    summary: PropTypes.string,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.any]),
    placeholder: PropTypes.string,
    submenuOptionsFunc: PropTypes.func,
    onSubmenuSelect: PropTypes.func,
    getSelectedSubmenu: PropTypes.func,
    className: PropTypes.string,
    style: PropTypes.object,
    closeOnSelect: PropTypes.bool,
};

export default AdvancedDropDownList;
